const API_URL = process.env.REACT_APP_API_URL || '';

const TOKEN_KEY = 'ct_auth_token';
const REFRESH_TOKEN_KEY = 'ct_refresh_token';

export class ApiError extends Error {
  constructor(message, payload = {}, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    this.reason = payload.reason || '';
    this.moderation = payload.moderation || [];
  }
}

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearAuthTokens() {
  setAuthToken('');
  setRefreshToken('');
}

let refreshPromise = null;

async function refreshAuthToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = request('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      skipAuthRefresh: true,
    })
      .then((payload) => {
        setAuthToken(payload.token);
        setRefreshToken(payload.refresh_token);
        return payload;
      })
      .catch((error) => {
        clearAuthTokens();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

async function request(path, options = {}) {
  const token = getAuthToken();
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      headers,
      ...options,
    });
  } catch {
    throw new Error('api_unreachable');
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401 && !options.skipAuthRefresh) {
      try {
        await refreshAuthToken();
        return request(path, { ...options, skipAuthRefresh: true });
      } catch {
        clearAuthTokens();
      }
    } else if (response.status === 401) {
      clearAuthTokens();
    }
    throw new ApiError(payload.error || 'request_failed', payload, response.status);
  }

  return payload;
}

export const api = {
  apiUrl: API_URL,
  refreshAuth: refreshAuthToken,
  getHealth: () => request('/api/health'),
  trackVisit: (payload) =>
    request('/api/site-visits', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuthRefresh: true,
    }),
  requestOtp: (email, language = 'en') =>
    request('/api/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email, language }),
    }),
  verifyOtp: (email, otp) =>
    request('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    }),
  logout: () =>
    request('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: getRefreshToken() }),
      skipAuthRefresh: true,
    }),
  getMe: () => request('/api/auth/me'),
	  getProfile: () => request('/api/profile'),
  getUserNotifications: () => request('/api/user-notifications'),
  getUserNotificationSummary: () => request('/api/user-notifications/summary'),
  markUserNotificationRead: (id) =>
    request(`/api/user-notifications/${id}/read`, {
      method: 'PATCH',
    }),
	  updateProfile: (data) =>
    request('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteAccount: () =>
    request('/api/account', {
      method: 'DELETE',
    }),
  searchLocations: (query, language = 'en') =>
    request(`/api/locations/search?q=${encodeURIComponent(query)}&language=${encodeURIComponent(language)}`),
  getCelebrities: (search = '') =>
    request(`/api/celebrities${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getCelebrityProfile: (slug) => request(`/api/celebrities/${encodeURIComponent(slug)}`),
  refreshCelebrityFeed: (slug) =>
    request(`/api/celebrities/${encodeURIComponent(slug)}/feed-refresh`, {
      method: 'POST',
    }),
  createCelebrity: (data) =>
    request('/api/celebrities', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createCelebrityPick: (data) =>
    request('/api/celebrity-picker', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  pickAndFollowCelebrity: (data) =>
    request('/api/celebrity-picker/follow', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  searchCelebrityPicker: (query) =>
    request(`/api/celebrity-picker/search?query=${encodeURIComponent(query)}`),
  getFollows: () => request('/api/follows'),
  refreshFollowedFeeds: () =>
    request('/api/follows/refresh', {
      method: 'POST',
    }),
  followCelebrity: (id) =>
    request(`/api/follows/${id}`, {
      method: 'POST',
    }),
  unfollowCelebrity: (id) =>
    request(`/api/follows/${id}`, {
      method: 'DELETE',
    }),
  getLists: () => request('/api/lists'),
  createList: (data) =>
    request('/api/lists', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getListItems: (listId) => request(`/api/lists/${listId}/items`),
  addListItem: (listId, celebrityId, source = 'direct') =>
    request(`/api/lists/${listId}/items/${celebrityId}`, {
      method: 'POST',
      body: JSON.stringify({ source }),
    }),
  removeListItem: (listId, celebrityId) =>
    request(`/api/lists/${listId}/items/${celebrityId}`, {
      method: 'DELETE',
    }),
  getSocialProfiles: (celebrityId) => request(`/api/celebrities/${celebrityId}/social-profiles`),
  createSocialProfile: (celebrityId, data) =>
    request(`/api/celebrities/${celebrityId}/social-profiles`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSocialProfile: (id, data) =>
    request(`/api/social-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  searchSocialProfiles: (platform, query) =>
    request(`/api/social/search?platform=${encodeURIComponent(platform)}&query=${encodeURIComponent(query)}`),
  discoverSocialProfiles: (platform, query, options = {}) => {
    const params = new URLSearchParams({
      platform,
      query,
      limit: String(options.limit || 12),
      offset: String(options.offset || 0),
      verified_only: options.verifiedOnly === false ? '0' : '1',
    });
    return request(`/api/social/discover?${params.toString()}`);
  },
  getSocialProfileDetail: (platform, handle) =>
    request(`/api/social/profile-detail?platform=${encodeURIComponent(platform)}&handle=${encodeURIComponent(handle)}`),
  getFeed: (options = {}) => {
    const params = new URLSearchParams({
      limit: String(options.limit || 30),
      offset: String(options.offset || 0),
    });
    if (options.seed) params.set('seed', String(options.seed));
    if (options.language) params.set('language', String(options.language));
    return request(`/api/feed?${params.toString()}`);
  },
  getFeedStatus: () => request('/api/feed/status'),
  getEvent: (id) => request(`/api/events/${id}`),
  getEventAiMatch: (id, language = 'en') =>
    request(`/api/events/${id}/ai-match?language=${encodeURIComponent(language)}`),
  matchEventLook: (id, language = 'en', options = {}) =>
    request(`/api/events/${id}/ai-match`, {
      method: 'POST',
      body: JSON.stringify({ language, force: options.force === true }),
    }),
  getUserPhotos: () => request('/api/user-photos'),
  createUserPhoto: (data) =>
    request('/api/user-photos', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateUserPhoto: (id, data) =>
    request(`/api/user-photos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteUserPhoto: (id) =>
    request(`/api/user-photos/${id}`, {
      method: 'DELETE',
    }),
  createTryOnPreview: (eventId, userPhotoId, language = 'en', options = {}) =>
    request(`/api/events/${eventId}/try-on`, {
      method: 'POST',
      body: JSON.stringify({
        user_photo_id: userPhotoId,
        language,
        notify_email: options.notifyEmail === true,
        try_on_type: options.tryOnType || 'replica',
        selected_product_ids: options.selectedProductIds || [],
      }),
    }),
  getTryOnPreviews: (options = {}) => {
    const params = new URLSearchParams();
    if (options.eventId) params.set('event_id', String(options.eventId));
    return request(`/api/try-on-previews${params.toString() ? `?${params.toString()}` : ''}`);
  },
  getTryOnPreview: (id) => request(`/api/try-on-previews/${id}`),
  deleteTryOnPreview: (id) =>
    request(`/api/try-on-previews/${id}`, {
      method: 'DELETE',
    }),
  updateTryOnPublic: (id, isPublic, options = {}) =>
    request(`/api/try-on-previews/${id}/public`, {
      method: 'PATCH',
      body: JSON.stringify({
        is_public: isPublic === true,
        use_profile_name: options.useProfileName === true,
        public_consent: options.publicConsent === true,
      }),
    }),
  getTryOnFeed: (options = {}) => {
    const params = new URLSearchParams({
      limit: String(options.limit || 12),
      offset: String(options.offset || 0),
    });
    return request(`/api/try-on-feed?${params.toString()}`);
  },
  getPublicSeoCelebrity: (slug) =>
    request(`/api/public/seo/celebrities/${encodeURIComponent(slug)}`, { skipAuthRefresh: true }),
  getPublicSeoLook: (id) =>
    request(`/api/public/seo/looks/${encodeURIComponent(id)}`, { skipAuthRefresh: true }),
  getPublicTryOn: (shareId) => request(`/api/public/try-ons/${encodeURIComponent(shareId)}`, { skipAuthRefresh: true }),
  getCreatorPreview: (token) => request(`/api/public/creator-previews/${encodeURIComponent(token)}`, { skipAuthRefresh: true }),
  approveCreatorPreview: (token, data) =>
    request(`/api/public/creator-previews/${encodeURIComponent(token)}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuthRefresh: true,
    }),
  removeCreatorPreview: (token, data = {}) =>
    request(`/api/public/creator-previews/${encodeURIComponent(token)}/remove`, {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuthRefresh: true,
    }),
  createEvent: (data, adminKey) =>
    request('/api/admin/events', {
      method: 'POST',
      headers: {
        'x-admin-key': adminKey,
      },
      body: JSON.stringify(data),
    }),
};
